import {
  ChatbotRankingCriteria,
  type ChatbotRankingCriteria as ChatbotRankingCriteriaType,
} from '../../types/lac';
import { dateInputToUnixMillis } from '../../utils/dateTimeUtils';

export const ALL_TENANTS_FILTER = 'all';

/** Maximum supported by the top chatbots API (no offset/pagination). */
export const LAC_CHATBOTS_TAB_TOP_LIMIT = 25;

export const LAC_CHATBOT_CRITERIA_OPTIONS = [
  ChatbotRankingCriteria.Messages,
  ChatbotRankingCriteria.Conversations,
  ChatbotRankingCriteria.Members,
] as const satisfies readonly ChatbotRankingCriteriaType[];

export const DEFAULT_LAC_CHATBOT_CRITERIA: ChatbotRankingCriteriaType =
  ChatbotRankingCriteria.Messages;

export type LacChatbotsFilterState = {
  tenant: string;
  criteria: ChatbotRankingCriteriaType;
  fromDay: string;
  toDay: string;
};

function parseDayParam(searchParams: URLSearchParams, key: string): string {
  const value = searchParams.get(key)?.trim() ?? '';
  return dateInputToUnixMillis(value) !== undefined ? value : '';
}

function isChatbotRankingCriteria(
  value: string,
): value is ChatbotRankingCriteriaType {
  return LAC_CHATBOT_CRITERIA_OPTIONS.some((option) => option === value);
}

export function parseLacChatbotsFilterFromSearchParams(
  searchParams: URLSearchParams,
): LacChatbotsFilterState {
  const tenantRaw = searchParams.get('tenant')?.trim() ?? '';
  const criteriaRaw = searchParams.get('criteria')?.trim() ?? '';
  const fromDay = parseDayParam(searchParams, 'from');
  const toDay = parseDayParam(searchParams, 'to');

  return {
    tenant:
      tenantRaw !== '' && tenantRaw !== ALL_TENANTS_FILTER
        ? tenantRaw
        : ALL_TENANTS_FILTER,
    criteria: isChatbotRankingCriteria(criteriaRaw)
      ? criteriaRaw
      : DEFAULT_LAC_CHATBOT_CRITERIA,
    fromDay,
    toDay: fromDay !== '' && toDay !== '' && toDay >= fromDay ? toDay : '',
  };
}

export function buildLacChatbotsSearchParams({
  tenant,
  criteria,
  fromDay,
  toDay,
}: {
  tenant: string;
  criteria: ChatbotRankingCriteriaType;
  fromDay: string;
  toDay: string;
}): URLSearchParams {
  const params = new URLSearchParams();

  if (tenant !== ALL_TENANTS_FILTER && tenant.trim() !== '') {
    params.set('tenant', tenant.trim());
  }

  if (criteria !== DEFAULT_LAC_CHATBOT_CRITERIA) {
    params.set('criteria', criteria);
  }

  if (fromDay !== '' && toDay !== '' && toDay >= fromDay) {
    params.set('from', fromDay);
    params.set('to', toDay);
  }

  return params;
}
